import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Field,
  MoneyInput,
  SelectField,
  Textarea,
} from '@/components/ui/controls'
import { getProduct, updateProduct } from '../../services/product'
import { getCurrencies, type CurrencyOption } from '../../services/config'
import type { Product } from '../../types'

export default function ProductEdit() {
  const { id } = useParams<{ id: string }>(),
    navigate = useNavigate()
  const [product, setProduct] = useState<Product | null>(null),
    [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false)
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([])
  const [name, setName] = useState(''),
    [description, setDescription] = useState(''),
    [price, setPrice] = useState(''),
    [currency, setCurrency] = useState('USD')
  useEffect(() => {
    getCurrencies()
      .then((res) => setCurrencyOptions(res.data.currencies))
      .catch(() =>
        setCurrencyOptions(
          ['USD', 'CNY', 'HKD'].map((code) => ({ code, label: code })),
        ),
      )
  }, [])
  useEffect(() => {
    if (id)
      getProduct(id)
        .then((res) => {
          setProduct(res.data)
          setName(res.data.name)
          setDescription(res.data.description || '')
          setPrice(String(res.data.price))
          setCurrency(res.data.currency)
        })
        .catch(() => toast.error('產品不存在'))
        .finally(() => setLoading(false))
  }, [id])
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim()) {
      toast.warning('請輸入產品名稱')
      return
    }
    if (!/^\d+(\.\d{1,2})?$/.test(price) || Number(price) < 0.01) {
      toast.warning('請輸入至少 0.01 的價格，最多兩位小數')
      return
    }
    if (!id) return
    setSaving(true)
    try {
      await updateProduct(id, { name, description, price, currency })
      toast.success('產品更新成功')
      navigate('/dashboard/products')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '更新失敗')
    } finally {
      setSaving(false)
    }
  }
  if (loading) return <div role="status">載入中…</div>
  if (!product) return <div>產品不存在</div>
  const visibleCurrencyOptions = currencyOptions.some(
    (c) => c.code === product.currency,
  )
    ? currencyOptions
    : [{ code: product.currency, label: product.currency }, ...currencyOptions]
  return (
    <div>
      <h2 className="mb-6 text-xl font-semibold">編輯產品</h2>
      <Card className="max-w-3xl">
        <form onSubmit={submit} className="space-y-5 px-5">
          <Field label="產品名稱" htmlFor="product-name" required>
            <Input
              id="product-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：100 API Credits"
            />
          </Field>
          <Field label="產品描述" htmlFor="product-description">
            <Textarea
              id="product-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="產品描述（可選）"
            />
          </Field>
          <Field label="價格" htmlFor="product-price" required>
            <div className="flex gap-2">
              <MoneyInput
                id="product-price"
                required
                min="0.01"
                step="0.01"
                value={price}
                onValueChange={setPrice}
              />
              <SelectField
                id="product-currency"
                aria-label="幣種"
                value={currency}
                onValueChange={setCurrency}
                options={visibleCurrencyOptions.map((c) => ({
                  value: c.code,
                  label: c.label,
                  disabled: c.code === 'IDR' && product.currency !== 'IDR',
                }))}
              />
            </div>
          </Field>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              儲存
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/products')}
            >
              取消
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
